import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Download, Layers, Paintbrush, GlassWater, ChevronRight, Loader2 } from "lucide-react";

const SHADE_CARDS = [
  {
    id: "prelaminated",
    name: "Prelaminated Particleboard-MDF Shade Card",
    shortName: "Laminate Shades",
    icon: Layers,
    color: "#C45B28",
    pdfUrl: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/cctuqnys_Prelaminated%20Particleboard-MDF%20Shade%20Card.pdf",
  },
  {
    id: "fabric",
    name: "Privacy Fabric Screen Shade Card",
    shortName: "Fabric Shades",
    icon: Paintbrush,
    color: "#8B4A8C",
    pdfUrl: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/rfaqpkp2_Privacy%20Fabric%20Screen%20Shade%20Card.pdf.pdf",
  },
  {
    id: "glass",
    name: "Privacy Glass Screen Shade Card",
    shortName: "Glass Shades",
    icon: GlassWater,
    color: "#2A7A8A",
    pdfUrl: "https://customer-assets.emergentagent.com/job_037ecef3-a2b2-42f0-a4d9-2cc65b6aab87/artifacts/zschupiv_Privacy%20Glass%20Screen%20Shade%20Card.pdf",
  },
];

export { SHADE_CARDS };

function ShadeModal({ card, onClose }) {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", handleKey);
    };
  }, [onClose]);

  const googleViewerUrl = `https://docs.google.com/gview?url=${encodeURIComponent(card.pdfUrl)}&embedded=true`;

  return (
    <motion.div
      className="fixed inset-0 z-[9999] flex items-center justify-center"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />

      <motion.div
        className="relative w-[95vw] max-w-5xl h-[85vh] bg-white rounded-lg shadow-2xl flex flex-col overflow-hidden"
        initial={{ scale: 0.9, y: 30 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 30 }}
        transition={{ duration: 0.25 }}
        data-testid={`shade-modal-${card.id}`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 md:px-6 py-3 border-b bg-[#0F172A] text-white shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <card.icon className="w-5 h-5 shrink-0" style={{ color: card.color }} />
            <h2 className="text-sm md:text-base font-bold truncate">{card.name}</h2>
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-3">
            <a
              href={card.pdfUrl}
              download
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-xs font-bold rounded text-white transition-all hover:brightness-110"
              style={{ backgroundColor: card.color }}
              data-testid={`shade-download-${card.id}`}
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors"
              data-testid={`shade-close-${card.id}`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* PDF Viewer */}
        <div className="flex-1 relative bg-[#f1f1f1]">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#f8f8f8] z-10">
              <Loader2 className="w-8 h-8 animate-spin mb-3" style={{ color: card.color }} />
              <p className="text-sm text-[#64748B]">Loading catalogue...</p>
            </div>
          )}
          <iframe src={googleViewerUrl}
            title={card.name}
            className="w-full h-full border-0"
            loading="lazy"
            onLoad={() => setLoading(false)}
            data-testid={`shade-pdf-${card.id}`}
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

export { ShadeModal };

export default function ShadeCardSidebar() {
  const [activeCard, setActiveCard] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <>
      {/* Desktop / tablet floating sidebar (3 separate icons) */}
      <div
        className="hidden md:flex fixed right-0 top-1/2 -translate-y-1/2 z-[999] flex-col items-end gap-0"
        data-testid="shade-card-sidebar"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        {SHADE_CARDS.map((card, index) => (
          <motion.button
            key={card.id}
            onClick={() => setActiveCard(card)}
            className="group flex items-center gap-0 overflow-hidden cursor-pointer border-b border-white/20 last:border-0"
            style={{ backgroundColor: card.color }}
            data-testid={`shade-btn-${card.id}`}
            initial={false}
          >
            <AnimatePresence>
              {isExpanded && (
                <motion.span
                  className="text-white text-xs font-semibold whitespace-nowrap pl-4 pr-2 py-3"
                  initial={{ width: 0, opacity: 0 }}
                  animate={{ width: "auto", opacity: 1 }}
                  exit={{ width: 0, opacity: 0 }}
                  transition={{ duration: 0.25, delay: index * 0.05 }}
                >
                  {card.shortName}
                </motion.span>
              )}
            </AnimatePresence>
            <div className="w-12 h-12 flex items-center justify-center shrink-0">
              <card.icon className="w-5 h-5 text-white" />
            </div>
          </motion.button>
        ))}

        {!isExpanded && (
          <motion.div
            className="absolute -left-5 top-1/2 -translate-y-1/2 w-5 h-10 bg-[#0F172A]/70 rounded-l flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 0.5 }}
          >
            <ChevronRight className="w-3 h-3 text-white/70 rotate-180" />
          </motion.div>
        )}
      </div>

      {/* Mobile: 3 vertical strips on right edge — Laminate / Fabric / Glass — total height matches the Talk-to-Expert strip on the left */}
      <div
        className="md:hidden fixed right-0 top-1/2 -translate-y-1/2 z-50 flex flex-col shadow-lg"
        data-testid="shade-card-strips-mobile"
      >
        {SHADE_CARDS.map((card, idx) => {
          const Icon = card.icon;
          const labelWord = card.shortName.split(" ")[0]; // "Laminate" | "Fabric" | "Glass"
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setActiveCard(card)}
              className={`text-white py-2 px-1 flex flex-col items-center justify-center gap-1.5 active:opacity-85 transition-opacity ${
                idx === 0 ? "rounded-tl-md" : ""
              } ${idx === SHADE_CARDS.length - 1 ? "rounded-bl-md" : ""}`}
              style={{ background: card.color, width: "20px", height: "60px" }}
              data-testid={`shade-card-mobile-${card.id}`}
              aria-label={card.shortName}
            >
              <Icon className="w-3 h-3 shrink-0" />
              <span
                className="text-[8px] font-bold uppercase tracking-wider whitespace-nowrap leading-none"
                style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}
              >
                {labelWord}
              </span>
            </button>
          );
        })}
      </div>

      <AnimatePresence>
        {activeCard && (
          <ShadeModal card={activeCard} onClose={() => setActiveCard(null)} />
        )}
      </AnimatePresence>
    </>
  );
}
